import {
    Button,
    Card,
    CircularProgress,
    Grid,
    Snackbar,
    Theme,
    Typography,
    withStyles,
    TableContainer,
    Table,
    TableBody,
    TableRow,
    TableCell,
    TableHead,
    Paper,
    IconButton,
} from '@material-ui/core';

import Alert from '@material-ui/lab/Alert';
import { CSSProperties } from '@material-ui/core/styles/withStyles';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import DeleteIcon from '@material-ui/icons/Delete';
import React from 'react';
import compose from 'recompose/compose';
import { connect } from 'react-redux';
import moment from 'moment';

const styles = (theme: Theme): Record<string, CSSProperties> => ({
    component: {
        position: 'relative',
        padding: 20,
        marginBottom: 50,
    },
    loading: {
        position: 'absolute',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 500,
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
    },
    menu: {
        width: 200,
    },
    upload: {
        marginBottom: theme.spacing(1),
    },
    heading: {
        marginBottom: theme.spacing(3),
    },
    dropdownSelect: {
        paddingTop: 12,
        paddingBottom: 12,
    },
});

interface IProps {
    classes?: any;
    admin: any;
    actions: any;
}

interface IState {
    loading: boolean;
    showFileInput: boolean;
    status: string;
}

class WiseconnReport extends React.Component<IProps, IState> {
    constructor(props: IProps) {
        super(props);

        this.state = {
            loading: false,
            showFileInput: false,
            status: '',
        };
    }

    public componentDidMount() {
        const { actions } = this.props;

        actions.getWiseconnFiles();
    }

    public render() {
        const {
            classes,
            admin: { wiseconnFiles },
        } = this.props;
        const { loading, showFileInput, status } = this.state;

        return (
            <Card square={true} className={classes.component}>
                {loading && (
                    <div className={classes.loading}>
                        <CircularProgress color="primary" />
                    </div>
                )}
                <Typography variant="h6" className={classes.heading}>
                    Wiseconn Reports
                </Typography>
                <form noValidate={true}>
                    <Grid container={true} direction="column" alignItems="flex-start">
                        <Grid item={true}>
                            <Button
                                size="large"
                                variant="contained"
                                color="primary"
                                onClick={this.handleClickUpload}
                                className={classes.upload}
                            >
                                Upload Wiseconn Reports
                            </Button>
                            <Snackbar
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'left',
                                }}
                                open={status !== ''}
                                autoHideDuration={5000}
                                onClose={this.handleCloseSnackbar}
                                message="Note archived"
                            >
                                <div>
                                    {status !== '' && (
                                        <Alert severity={status === 'success' ? 'success' : 'error'}>
                                            {status === 'success' ? 'Uploaded to Wiseconn S3 bucket' : 'Error uploading file'}
                                        </Alert>
                                    )}
                                </div>
                            </Snackbar>
                        </Grid>
                        {showFileInput && (
                            <input
                                type="file"
                                multiple={true}
                                id="wiseconnreport"
                                style={{ display: 'none' }}
                                onChange={this.handleChangeUpload}
                            />
                        )}
                    </Grid>
                </form>
                <br />
                <Typography className={classes.heading} variant="h6">
                    S3 Bucket Files
                </Typography>
                <TableContainer component={Paper} style={{ maxHeight: 300 }}>
                    <Table size="small" stickyHeader={true}>
                        <TableHead>
                            <TableRow>
                                <TableCell scope="row">File Name</TableCell>
                                <TableCell>Last Modified</TableCell>
                                <TableCell />
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {wiseconnFiles.map((file: any, index: number) => {
                                return (
                                    <TableRow key={index}>
                                        <TableCell scope="row">{file.Key}</TableCell>
                                        <TableCell>{moment(file.LastModified).format('MM/DD/YYYY h:mm A')}</TableCell>
                                        <TableCell>
                                            <IconButton
                                                color="primary"
                                                aria-label="download"
                                                component="span"
                                                onClick={() => this.handleFileDownload(file.Key)}
                                            >
                                                <CloudDownloadIcon />
                                            </IconButton>
                                        </TableCell>
                                        <TableCell>
                                            <IconButton
                                                color="primary"
                                                aria-label="delete"
                                                component="span"
                                                onClick={() => this.handleFileDelete(file.Key)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
                <a id="file-download" style={{ display: 'none' }} />
            </Card>
        );
    }

    private handleFileDelete = (key: string) => {
        const { actions } = this.props;

        actions.deleteWiseconnFile(key).then(() => {
            actions.getWiseconnFiles();
        });
    };

    private handleFileDownload = (key: string) => {
        const { actions } = this.props;
        const downloadElement: any = document.getElementById('file-download');

        actions.getWiseconnFile(key).then((response: any) => {
            const { data } = response;

            if (downloadElement) {
                const blob = new Blob([new Uint8Array(data.Body.data)], { type: data.ContentType });
                downloadElement.href = window.URL.createObjectURL(blob);
                downloadElement.download = key;
                downloadElement.click();
            }
        });
    };

    private handleCloseSnackbar = () => {
        this.setState({ status: '' });
    };

    private handleChangeUpload = (event: any) => {
        const { actions } = this.props;
        const {
            target: { files },
        } = event;
        const uploadRequests: any = [];

        this.setState({ loading: true }, () => {
            for (let index = 0; index < files.length; index++) {
                const file = files[index];
                const { name } = file;
                uploadRequests.push(actions.postWiseconnReport(file, name));
            }

            Promise.all(uploadRequests)
                .then(() => {
                    this.setState({ loading: false, showFileInput: false, status: 'success' }, () => {
                        actions.getWiseconnFiles();
                    });
                })
                .catch(() => {
                    this.setState({ loading: false, showFileInput: false, status: 'error' });
                });
        });
    };

    private handleClickUpload = () => {
        this.setState({ showFileInput: true }, () => {
            const wiseconnReport = document.getElementById('wiseconnreport');

            if (wiseconnReport) {
                wiseconnReport.click();
            }
        });
    };
}

const mapStoreToProps = (state: any) => {
    return {
        actions: state.admin.actions,
        admin: state.admin.data,
    };
};

export default compose<IProps, {}>(connect(mapStoreToProps, null), withStyles(styles))(WiseconnReport);
